/**
 * HermesKit 对外抛出的错误（对应 Swift 的 HermesError）。界面只需要区分“要重新登录”“连不上”“服务端拒绝”三类。
 */
export type HermesErrorKind =
  | "authenticationRequired" // 凭据失效或被拒绝，需要重新登录
  | "transport" // 网络/握手失败，可以重试
  | "rpc" // 网关返回的 JSON-RPC 错误
  | "timeout"
  | "notConnected"
  | "http" // HTTP 层错误（登录、ticket 等 REST 调用）
  | "decoding"
  | "invalidConfiguration"
  | "cancelled";

export class HermesError extends Error {
  readonly kind: HermesErrorKind;
  readonly code?: number;
  readonly status?: number;
  readonly data?: unknown;
  readonly detail: string;

  constructor(kind: HermesErrorKind, detail = "", extra: { code?: number; status?: number; data?: unknown } = {}) {
    super(describe(kind, detail, extra));
    this.name = "HermesError";
    this.kind = kind;
    this.detail = detail;
    this.code = extra.code;
    this.status = extra.status;
    this.data = extra.data;
  }

  static authenticationRequired(m: string) { return new HermesError("authenticationRequired", m); }
  static transport(m: string) { return new HermesError("transport", m); }
  static rpc(code: number | undefined, m: string, data?: unknown) { return new HermesError("rpc", m, { code, data }); }
  static timeout(method: string, seconds: number) { return new HermesError("timeout", `${method}`, { code: seconds }); }
  static notConnected() { return new HermesError("notConnected"); }
  static http(status: number, m: string) { return new HermesError("http", m, { status }); }
  static decoding(m: string) { return new HermesError("decoding", m); }
  static invalidConfiguration(m: string) { return new HermesError("invalidConfiguration", m); }
  static cancelled() { return new HermesError("cancelled"); }

  get needsLogin(): boolean { return this.kind === "authenticationRequired"; }
  /** JSON-RPC -32601：服务端没有这个方法（老版本 Hermes） */
  get isMethodNotFound(): boolean { return this.kind === "rpc" && this.code === -32601; }
}

function describe(kind: HermesErrorKind, m: string, extra: { code?: number; status?: number }): string {
  switch (kind) {
    case "authenticationRequired": return `需要重新登录：${m}`;
    case "transport": return `连接失败：${m}`;
    case "rpc": return m;
    case "timeout": return `请求超时（${extra.code ?? 0}s）：${m}`;
    case "notConnected": return "尚未连接到 Hermes";
    case "http": return `HTTP ${extra.status ?? 0}：${m}`;
    case "decoding": return `数据解析失败：${m}`;
    case "invalidConfiguration": return m;
    case "cancelled": return "已取消";
  }
}

export function asHermesError(error: unknown): HermesError {
  if (error instanceof HermesError) return error;
  if (error instanceof Error) return HermesError.transport(error.message);
  return HermesError.transport(String(error));
}

/** 给界面显示的一句话 */
export function errorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}
